// src/services/userService.js
import apiService from '../utils/apiInterceptor';

const userService = {
  getSchedoxSyncStatus: async (userId) => apiService.get(`/admin/users/${userId}/schedox-sync`),
  startSchedoxSync: async (userId) => apiService.post(`/admin/users/${userId}/schedox-sync`),
  /**
   * Get all customers (non-admin users)
   * @returns {Promise} Promise object with user data
   */
  getAllCustomers: async () => {
    return await apiService.get('/admin/users/customers');
  },
  /**
 * Update user password
 * @param {number|string} userId - User ID
 * @param {Object} passwordData - Password data containing current_password, password, and password_confirmation
 * @returns {Promise<Object>} Promise resolving to the API response
 */
 updatePassword :async (userId, passwordData) => {
  try {
    return await apiService.post(`/update-password/${userId}`, passwordData);
  } catch (error) {
    console.error(`Error updating password for user ${userId}:`, error);
    throw error;
  }
},

  /**
   * Get user by ID
   * @param {number} id - User ID
   * @returns {Promise} Promise object with user data
   */
  getUserById: async (id) => {
    return await apiService.get(`/admin/users/${id}`);
  },
  
  /**
   * Update user status (active/inactive)
   * @param {number} id - User ID
   * @param {number} status - Status (1 for active, 0 for inactive)
   * @returns {Promise} Promise object with result
   */
  updateUserStatus: async (id, status) => {
    return await apiService.post('/admin/user/change-user-status', {
      user_id: id,
      status: status
    });
  },
   /**
   * Get all customers/users with optional filtering and pagination
   * @param {Object} params - Filter and pagination parameters
   * @returns {Promise<Object>} Promise resolving to user data
   */
   getAllCustomers: async (params = {}) => {
    try {
      const response = await apiService.get('/admin/users/customers', { params });
      return response;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Get user by ID
   * @param {number|string} userId - User ID
   * @returns {Promise<Object>} Promise resolving to user data
   */
  getUserById: async (userId) => {
    try {
      const response = await apiService.get(`/admin/user/edit/${userId}`);
      return response.user || response;
    } catch (error) {
      console.error(`Error fetching user with ID ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new user
   * @param {Object} userData - User data to create
   * @returns {Promise<Object>} Promise resolving to the created user
   */
  createUser: async (userData) => {
    try {
      const response = await apiService.post('/admin/user/create', userData);
      return response;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  /**
   * Update an existing user
   * @param {number|string} userId - User ID to update
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} Promise resolving to the updated user
   */
  updateUser: async (userId, userData) => {
    try {
      const response = await apiService.post(`/admin/user/edit/${userId}`, userData);
      return response;
    } catch (error) {
      console.error(`Error updating user with ID ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a user
   * @param {number|string} userId - User ID to delete
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  deleteUser: async (userId) => {
    try {
      const response = await apiService.get(`/admin/user/delete/${userId}`);
      return response;
    } catch (error) {
      console.error(`Error deleting user with ID ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Update user status (active/inactive)
   * @param {number|string} userId - User ID
   * @param {number} status - Status value (1 for active, 0 for inactive)
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  updateUserStatus: async (userId, status) => {
    try {
      const response = await apiService.post('/admin/user/change-user-status', {
        user_id: userId,
        status
      });
      return response;
    } catch (error) {
      console.error(`Error updating status for user with ID ${userId}:`, error);
      throw error;
    }
  },

  getPaymentProfile: async (userId) => {
    try {
      return await apiService.get(`/admin/users/${userId}/payment-profile`);
    } catch (error) {
      console.error(`Error fetching payment profile for user ${userId}:`, error);
      throw error;
    }
  },

  updatePaymentProfile: async (userId, data) => {
    try {
      return await apiService.post(`/admin/users/${userId}/payment-profile`, data);
    } catch (error) {
      console.error(`Error updating payment profile for user ${userId}:`, error);
      throw error;
    }
  },

  uploadAvatar: async (userId, file) => {
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      return await apiService.post(`/admin/users/${userId}/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error(`Error uploading avatar for user ${userId}:`, error);
      throw error;
    }
  },

  createPaymentSetupLink: async (userId, sendEmail = true, accountType = 'stripe_1') => {
    try {
      return await apiService.post(`/admin/users/${userId}/payment-setup-link`, {
        send_email: sendEmail,
        account_type: accountType
      });
    } catch (error) {
      console.error(`Error creating payment setup link for user ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Export users to CSV/Excel
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  exportUsers: async () => {
    try {
      const response = await apiService.get('/export/users');
      return response;
    } catch (error) {
      console.error('Error exporting users:', error);
      throw error;
    }
  },

  /**
   * Impersonate a user (login as this user)
   * @param {number|string} userId - User ID to impersonate
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  impersonateUser: async (userId) => {
    try {
      const response = await apiService.get(`/view-properties/${userId}`);
      return { success: true, data: response };
    } catch (error) {
      console.error(`Error impersonating user with ID ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Get user statistics
   * @param {number|string} userId - User ID
   * @returns {Promise<Object>} Promise resolving to user statistics
   */
  getUserStats: async (userId) => {
    try {
      // This endpoint would need to be implemented on the backend
      const response = await apiService.get(`/admin/user/stats/${userId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching statistics for user with ID ${userId}:`, error);
      // Return mock data since this endpoint might not exist yet
      return {
        data: {
          properties: Math.floor(Math.random() * 10),
          activeOrders: Math.floor(Math.random() * 5),
          completedOrders: Math.floor(Math.random() * 20),
          totalSpent: (Math.random() * 1000).toFixed(2),
          lastOrder: new Date().toISOString()
        }
      };
    }
  },

  /**
   * Get property ranges (for dropdown in user forms)
   * @returns {Promise<Object>} Promise resolving to property ranges
   */
  getPropertyRanges: async () => {
    try {
       return {
        data: [
          { id: 1, range: '1-5 Properties' },
          { id: 2, range: '6-10 Properties' },
          { id: 3, range: '11-25 Properties' },
          { id: 4, range: '26-50 Properties' },
          { id: 5, range: '51+ Properties' }
        ]
      };
    } catch (error) {
      console.error('Error fetching property ranges:', error);
      return {
        data: [
          { id: 1, range: '1-5 Properties' },
          { id: 2, range: '6-10 Properties' },
          { id: 3, range: '11-25 Properties' },
          { id: 4, range: '26-50 Properties' },
          { id: 5, range: '51+ Properties' }
        ]
      };
    }
  },
// Add these methods to your userService.js file

/**
 * Get all user groups
 * @returns {Promise<Object>} User groups data
 */
getUserGroups: async (params = {}) => {
  try {
    const response = await apiService.get('/admin/user-groups', { params });
    return response;
  } catch (error) {
    console.error('Error fetching user groups:', error);
    throw error;
  }
},

/**
 * Get a specific user group with its members
 * @param {number} groupId - Group ID
 * @returns {Promise<Object>} User group details
 */
getUserGroupDetails: async (groupId) => {
  try {
    const response = await apiService.get(`/admin/user-groups/${groupId}`);
    return response;
  } catch (error) {
    console.error('Error fetching user group details:', error);
    throw error;
  }
},

/**
 * Create a new user group
 * @param {Object} groupData - Group data to create
 * @returns {Promise<Object>} Created group
 */
createUserGroup: async (groupData) => {
  try {
    const response = await apiService.post('/admin/user-groups', groupData);
    return response;
  } catch (error) {
    console.error('Error creating user group:', error);
    throw error;
  }
},

/**
 * Update an existing user group
 * @param {number} groupId - Group ID to update
 * @param {Object} groupData - Updated group data
 * @returns {Promise<Object>} Updated group
 */
updateUserGroup: async (groupId, groupData) => {
  try {
    const response = await apiService.put(`/admin/user-groups/${groupId}`, groupData);
    return response;
  } catch (error) {
    console.error('Error updating user group:', error);
    throw error;
  }
},

/**
 * Delete a user group
 * @param {number} groupId - Group ID to delete
 * @returns {Promise<Object>} Response data
 */
deleteUserGroup: async (groupId) => {
  try {
    const response = await apiService.delete(`/admin/user-groups/${groupId}`);
    return response;
  } catch (error) {
    console.error('Error deleting user group:', error);
    throw error;
  }
},

/**
 * Add users to a group
 * @param {number} groupId - Group ID
 * @param {Array} userIds - User IDs to add
 * @returns {Promise<Object>} Response data
 */
addUsersToGroup: async (groupId, userIds) => {
  try {
    const response = await apiService.post(`/admin/user-groups/${groupId}/users`, {
      user_ids: userIds
    });
    return response;
  } catch (error) {
    console.error('Error adding users to group:', error);
    throw error;
  }
},

/**
 * Remove users from a group
 * @param {number} groupId - Group ID
 * @param {Array} userIds - User IDs to remove
 * @returns {Promise<Object>} Response data
 */
removeUsersFromGroup: async (groupId, userIds) => {
  try {
    const response = await apiService.delete(`/admin/user-groups/${groupId}/users`, {
      data: { user_ids: userIds }
    });
    return response;
  } catch (error) {
    console.error('Error removing users from group:', error);
    throw error;
  }
},

/**
 * Get users who are not members of a specific group
 * @param {number} groupId - Group ID
 * @param {Object} params - Search and pagination parameters
 * @returns {Promise<Object>} Non-member users data
 */
getNonGroupMembers: async (groupId, params = {}) => {
  try {
    const response = await apiService.get(`/admin/user-groups/${groupId}/non-members`, { params });
    return response;
  } catch (error) {
    console.error('Error fetching non-group members:', error);
    throw error;
  }
},
  

 placeOrder : async (orderData) => {
  try {
    // Ensure we include dates from frontend
    const response = await apiService.post('/place-order', {
      userId: orderData.userId,
      placerId: orderData.placerId || orderData.userId,
      cart: orderData.cart,
      orderSchedule: orderData.orderSchedule || {}
    });
    
    return response;
  } catch (error) {
    console.error('Error placing order:', error);
    throw error;
  }
},
  confirmOrderDates : async (orderSchedule) => {
  try {
    const response = await apiService.post('/confirm-order-dates', {
      orderSchedule
    });
    return response;
  } catch (error) {
    console.error('Error confirming order dates:', error);
    throw error;
  }
}
};




export default userService;
